import { api } from '@/libs/axios/axios'
import {
  getSpecialtyModelsQuery,
  getConsultationQuery,
  getConsultationStatusQuery,
  getConsultationsQuery,
  getConsultationReportQuery,
  scheduleConsultationMutation,
  startConsultationRecordingMutation,
  requestConsultationReportMutation,
  cancelConsultationMutation,
  editConsultationReportMutation,
  finishConsultationRecordingMutation,
} from './graphql'
import device from '@/utils/device'

export const useConsultationsApi = () => {
  const graphqlUrl = '/graphql'

  const getSpecialtyModels = async () => {
    const { data } = await api.post(graphqlUrl, {
      query: getSpecialtyModelsQuery,
    })
    if (data.errors?.length > 0) {
      throw new Error(data.errors[0].message)
    }
    return data.data.getConsultationReportSpecialtiesAvailable
  }

  const getConsultation = async ({ consultationId }) => {
    const input = {
      consultationId,
    }
    const { data } = await api.post(graphqlUrl, {
      query: getConsultationQuery,
      variables: {
        input,
      },
    })
    if (data.errors?.length > 0) {
      throw new Error(data.errors[0].message)
    }
    return data.data.getConsultation
  }

  const getConsultationStatus = async ({ consultationId }) => {
    const input = {
      consultationId,
    }
    const { data } = await api.post(graphqlUrl, {
      query: getConsultationStatusQuery,
      variables: {
        input,
      },
    })
    if (data.errors?.length > 0) {
      throw new Error(data.errors[0].message)
    }
    return data.data.getConsultation
  }

  const getConsultations = async ({ page, limit, search, status, period }) => {
    const input = {
      page: page || 1,
      limit: limit || 7,
      ...(search && { search }),
      ...(status && { status }),
      ...(period && { period }),
    }
    const { data } = await api.post(graphqlUrl, {
      query: getConsultationsQuery,
      variables: {
        input,
      },
    })
    if (data.errors?.length > 0) {
      throw new Error(data.errors[0].message)
    }
    return data.data.searchConsultations
  }

  const getConsultationReport = async ({ consultationId, reportId }) => {
    const input = {
      consultationId,
      reportId,
    }

    const { data } = await api.post(graphqlUrl, {
      query: getConsultationReportQuery,
      variables: {
        input,
      },
    })
    if (data.errors?.length > 0) {
      throw new Error(data.errors[0].message)
    }
    return data.data.getConsultationReport
  }

  const scheduleConsultation = async ({ patientId, specialty, customPromptId }) => {
    const input = {
      patientId,
      plannedStartDate: new Date(),
      plannedEndDate: new Date(),
      metadata: {
        daai: {
          origin: 'daai-web',
          version: app.version,
          specialty: specialty || null,
          customPromptId: customPromptId || null,
          ...device,
        },
      },
    }
    const { data } = await api.post(graphqlUrl, {
      query: scheduleConsultationMutation,
      variables: {
        input,
      },
    })
    if (data.errors?.length > 0) {
      throw new Error(data.errors[0].message)
    }
    return data.data.scheduleConsultation
  }

  const startConsultationRecording = async ({ consultationId }) => {
    const input = {
      consultationId,
    }
    const { data } = await api.post(graphqlUrl, {
      query: startConsultationRecordingMutation,
      variables: {
        input,
      },
    })
    if (data.errors?.length > 0) {
      throw new Error(data.errors[0].message)
    }
    return data.data.startConsultationRecording
  }

  const requestConsultationReport = async ({
    consultationId,
    reason,
    notes,
    specialty,
    customPromptId,
  }) => {
    const input = {
      consultationId,
      ...(notes != null && notes !== '' && { notes }),
      ...(reason != null && reason !== '' && { reason }),
      ...(specialty != null && specialty !== '' && { specialty }),
      ...(customPromptId != null && customPromptId !== '' && { customPromptId }),
    }
    const { data } = await api.post(graphqlUrl, {
      query: requestConsultationReportMutation,
      variables: {
        input,
      },
    })
    if (data.errors?.length > 0) {
      throw new Error(data.errors[0].message)
    }
    return data?.data?.requestConsultationReport ?? data
  }

  const cancelConsultation = async ({ consultationId, endDate }) => {
    const input = {
      consultationId,
      endDate: endDate || new Date(),
    }
    const { data } = await api.post(graphqlUrl, {
      query: cancelConsultationMutation,
      variables: {
        input,
      },
    })
    if (data.errors?.length > 0) {
      throw new Error(data.errors[0].message)
    }
    return data.data.startConsultationRecording
  }

  const editConsultationReport = async ({
    consultationId,
    reportId,
    content,
  }) => {
    const input = {
      consultationId,
      reportId,
      content,
    }
    const { data } = await api.post(graphqlUrl, {
      query: editConsultationReportMutation,
      variables: {
        input,
      },
    })
    if (data.errors?.length > 0) {
      throw new Error(data.errors[0].message)
    }
    return data.data.editConsultationReport
  }

  const sendConsultationAudio = async ({
    consultationId,
    file,
    duration,
    recordingId,
  }) => {
    const formData = new FormData()
    formData.append('file', file)
    formData.append('duration', duration || 0)

    const { data } = await api.patch(
      `/consultations/${consultationId}/recordings/${recordingId}`,
      formData,
      {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      },
    )
    if (data.errors?.length > 0) {
      throw new Error(data.errors[0].message)
    }
    return data
  }

  const sendConsultationAudioChunk = async ({
    consultationId,
    recordingId,
    chunkIndex,
    file,
    duration,
  }) => {
    const formData = new FormData()
    formData.append('recording', file)
    formData.append('duration', duration)
    formData.append('chunkIndex', chunkIndex)

    const { data } = await api.post(
      `/consultations/${consultationId}/recordings/${recordingId}/chunks`,
      formData,
      {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      },
    )
    if (data.errors?.length > 0) {
      throw new Error(data.errors[0].message)
    }
    return data
  }

  const finishConsultationRecording = async ({
    consultationId,
    recordingId,
    notes,
    reason,
    specialty,
    customPromptId,
  }) => {
    const input = {
      consultationId,
      recordingId,
      notes,
      reason,
      specialty,
      customPromptId,
    }

    const { data } = await api.post(graphqlUrl, {
      query: finishConsultationRecordingMutation,
      variables: {
        input,
      },
    })
    if (data.errors?.length > 0) {
      throw new Error(data.errors[0].message)
    }
    return data.data.finishConsultationRecording
  }

  const sendConsultationExamFiles = async ({ consultationId, files }) => {
    const formData = new FormData()
    files.forEach((file) => {
      formData.append('files', file)
    })

    const { data } = await api.post(
      `/consultations/${consultationId}/exams/upload`,
      formData,
      {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      },
    )
    if (data.errors?.length > 0) {
      throw new Error(data.errors[0].message)
    }
    return data
  }

  return {
    getSpecialtyModels,
    getConsultation,
    getConsultationStatus,
    getConsultations,
    getConsultationReport,
    scheduleConsultation,
    startConsultationRecording,
    requestConsultationReport,
    cancelConsultation,
    editConsultationReport,
    sendConsultationAudio,
    sendConsultationAudioChunk,
    finishConsultationRecording,
    sendConsultationExamFiles,
  }
}
